/* eslint-disable max-lines-per-function */
import { useIsFocused } from '@react-navigation/native';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Appearance, Platform, StyleSheet } from 'react-native';
import styled from 'styled-components/native';

import { ELoginMethod } from '@webtrader/core/api/user/types';
import { DEMO_LOGINS, ELoginAs } from '@webtrader/core/constants/account';
import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { i18nText, useTranslation } from '@webtrader/core/i18n';
import { ALL_MOBILE_ROUTES } from '@webtrader/core/router/mobile';
import { alertService } from '@webtrader/core/services/alert';
import { authService, useAuth } from '@webtrader/core/services/auth';
import { companyService, useCompany } from '@webtrader/core/services/company';
import { configService } from '@webtrader/core/services/config';
import { demoCreationService } from '@webtrader/core/services/demoCreation';
import geolocationService from '@webtrader/core/services/geolocation/service';
import { useTheme } from '@webtrader/core/services/theme/v2';
import { EThemeScheme } from '@webtrader/core/themes';
import { normalizeSelectedTab } from '@webtrader/core/utils/get-selected-login-tab';
import { checkIsNative } from '@webtrader/core/validators/device';

import StretchedScrollView from '@/components/ScrollView/Stretched';
import Tabs, { ITabOption } from '@/components/Tabs';
import { navigate } from '@/utils/navigation';

import LoginForm from './Form';
import { useLoginFormHook } from './Hooks/use-login-form';
import LanguageDropdown from './LanguageDropdown';
import LoginAttempt from './LoginAttempt';
import MiscItem from './Misc';
import VersionText from './Version';
import LoginOtpForm from './login-otp-form';
import LoginSubTab from './login-sub-tabs';

interface IUpdatedSelectedTabParams {
  setSelectedTab: (loginAs: ELoginAs) => void;
  setNeedUpdateFields: (value: boolean) => void;
  initialContext: boolean;
  loginAs?: ELoginAs;
  companyLoginConfig: boolean;
  mt4LiveLoginConfig: boolean;
  mt4DemoLoginConfig: boolean;
  mt5LiveLoginConfig: boolean;
  mt5DemoLoginConfig: boolean;
}

const updateSelectedTab = ({
  setSelectedTab,
  setNeedUpdateFields,
  initialContext,
  loginAs,
  companyLoginConfig,
  mt4LiveLoginConfig,
  mt4DemoLoginConfig,
  mt5LiveLoginConfig,
  mt5DemoLoginConfig
}: IUpdatedSelectedTabParams) => {
  let loginAccountType: ELoginAs | undefined = undefined;
  if (initialContext) {
    if (companyLoginConfig) {
      loginAccountType = ELoginAs.LIVE;
    } else if (mt4LiveLoginConfig) {
      loginAccountType = ELoginAs.MT4_LIVE;
    } else if (mt4DemoLoginConfig) {
      loginAccountType = ELoginAs.MT4_DEMO;
    } else if (mt5LiveLoginConfig) {
      loginAccountType = ELoginAs.MT5_LIVE;
    } else if (mt5DemoLoginConfig) {
      loginAccountType = ELoginAs.MT5_DEMO;
    }
  } else if (loginAs) {
    loginAccountType = loginAs;
  }
  if (loginAccountType) {
    setSelectedTab(loginAccountType);
    setNeedUpdateFields(true);
    authService.setLoginAs(loginAccountType);
  }

  if (loginAccountType && !DEMO_LOGINS.includes(loginAccountType)) {
    authService.presetTextFields();
  }
};

const LoginScreen: FC = () => {
  const {
    i18n: { language }
  } = useTranslation();

  const { initialContext, navigateToSignUpForm, loginAs, loginMethod } = useAuth();
  const { company, loading, errorMessage, access } = useCompany();
  const { theme } = useTheme();
  const { colors, scheme } = theme;
  const { otpEnabled } = company || {};

  const { companyLoginConfig, mt4LiveLoginConfig, mt4DemoLoginConfig, mt5LiveLoginConfig, mt5DemoLoginConfig } = access;

  const [selectedTab, setSelectedTab] = useState<ELoginAs>(ELoginAs.LIVE);
  const [needUpdateFields, setNeedUpdateFields] = useState(true);
  const focused = useIsFocused();
  const { values, submitForm, setFieldValue } = useLoginFormHook({ selectedTab, loginMethod });
  const isDemo = DEMO_LOGINS.includes(selectedTab);

  const normalizedSelectedTab = normalizeSelectedTab({ selectedTab, companyAccess: access });

  useEffect(() => {
    if (errorMessage) {
      alertService.showAlert({
        message: errorMessage,
        onAfterCancel: async () => {
          if (checkIsNative()) {
            geolocationService.init();
          }
          await authService.reinit();
          companyService.init();
        }
      });
    }
  }, [errorMessage]);

  useEffect(() => {
    if (focused && navigateToSignUpForm) {
      const timer = setTimeout(() => {
        navigate(ALL_MOBILE_ROUTES.AUTH.SIGN_UP);
      }, 500);

      return () => {
        clearTimeout(timer);
      };
    }

    if (focused) {
      demoCreationService.reset();
    }

    return undefined;
  }, [focused, navigateToSignUpForm]);

  useEffect(() => {
    updateSelectedTab({
      setSelectedTab,
      setNeedUpdateFields,
      initialContext,
      loginAs,
      companyLoginConfig,
      mt4LiveLoginConfig,
      mt4DemoLoginConfig,
      mt5LiveLoginConfig,
      mt5DemoLoginConfig
    });
  }, [
    companyLoginConfig,
    initialContext,
    loginAs,
    mt4DemoLoginConfig,
    mt4LiveLoginConfig,
    mt5DemoLoginConfig,
    mt5LiveLoginConfig
  ]);

  const onTabChange = (nextValue: ELoginAs) => {
    if (nextValue in ELoginAs) {
      authService.setLoginAs(nextValue as ELoginAs);
      setNeedUpdateFields(true);
      setSelectedTab(nextValue);
      authService.setInitialContext(false);
      authService.setLoginMethod(ELoginMethod.PASSWORD);
    }
  };

  const options = useMemo<ITabOption<ELoginAs>[]>(() => {
    const list: ITabOption<ELoginAs>[] = [
      {
        label: i18nText('Live Account', language),
        value: ELoginAs.LIVE,
        testID: EDataTestId.TAB_LOGIN_ACCOUNT_TYPE_CRM
      }
    ];

    if (!companyLoginConfig) {
      list.pop();
    }

    const liveLoginType = authService.getLiveLoginTypeFromCompanyAccess({ mt5LiveLoginConfig, mt4LiveLoginConfig });

    if (liveLoginType) {
      list.push({
        label: i18nText('Live Account', language),
        value: liveLoginType,
        testID: EDataTestId.TAB_LOGIN_ACCOUNT_TYPE_LIVE
      });
    }

    const demoLoginType = authService.getDemoLoginTypeFromCompanyAccess({ mt5DemoLoginConfig, mt4DemoLoginConfig });

    if (demoLoginType) {
      list.push({
        label: i18nText('Demo Account', language),
        value: demoLoginType,
        testID: EDataTestId.TAB_LOGIN_ACCOUNT_TYPE_DEMO
      });
    }

    return list;
  }, [language, companyLoginConfig, mt4LiveLoginConfig, mt4DemoLoginConfig, mt5LiveLoginConfig, mt5DemoLoginConfig]);

  const onSubTabChange = (nextValue: ELoginMethod) => {
    authService.setLoginMethod(nextValue);
    setNeedUpdateFields(true);
    authService.setInitialContext(false);

    // reset sub form
    setFieldValue('email', '');
    setFieldValue('countryKey', '');
    setFieldValue('phoneNumber', '');
    setFieldValue('verificationCode', '');
  };

  const logoPath = useMemo(() => {
    if (colors) {
      switch (scheme) {
        case EThemeScheme.DARK:
          return company?.logoDarkThemePath;
        case EThemeScheme.SYSTEM:
          return Appearance.getColorScheme() === 'dark' ? company?.logoDarkThemePath : company?.logoPath;
        default:
          return company?.logoPath;
      }
    }

    return company?.logoPath;
  }, [colors, scheme, company]);

  const companyLogo = useMemo(() => {
    if (logoPath && logoPath.length > 0 && !loading) {
      const imgUrl = `${checkIsNative() ? configService.getCurrentStaticDomain() : ''}` + '/' + logoPath;

      return <LogoImage style={imageStyles.logo} source={{ uri: imgUrl }} testID={EDataTestId.LOGIN_COMPANY_LOGO} />;
    }

    return null;
  }, [logoPath, loading]);

  const isShowSubTab = selectedTab === ELoginAs.LIVE && otpEnabled;
  const isShowLoginOtpForm = isShowSubTab && [ELoginMethod.EMAIL, ELoginMethod.SMS].includes(loginMethod);

  return (
    <ScrollView showsVerticalScrollIndicator={false} showsHorizontalScrollIndicator={false}>
      <Screen>
        <LanguageDropdown />
        {companyLogo}
        <LoginAttempt />
        <TabCentralize>
          <Tabs value={normalizedSelectedTab} options={options} onChange={onTabChange} />
        </TabCentralize>
        {isShowSubTab && (
          <TabCentralize>
            <LoginSubTab selected={loginMethod} setSelected={onSubTabChange} />
          </TabCentralize>
        )}
        {isShowLoginOtpForm ? (
          <LoginOtpForm
            loginMethod={loginMethod}
            values={values}
            setFieldValue={setFieldValue}
            submitForm={submitForm}
          />
        ) : (
          <LoginForm
            isDemo={isDemo}
            isShowSubTab={isShowSubTab}
            needUpdateFields={needUpdateFields}
            selectedTab={selectedTab}
            values={values}
            setFieldValue={setFieldValue}
            setNeedUpdateFields={setNeedUpdateFields}
            submitForm={submitForm}
          />
        )}
        <MiscItem
          selectedTab={selectedTab}
          registrationUrl={companyService.getRegistrationUrl(selectedTab, Platform.OS)}
          setNeedUpdateFields={setNeedUpdateFields}
        />
        {checkIsNative() && <VersionText />}
      </Screen>
    </ScrollView>
  );
};

export default LoginScreen;

const imageStyles = StyleSheet.create({
  logo: {
    resizeMode: 'contain'
  }
});

const ScrollView = styled(StretchedScrollView)`
  flex: 1;
`;

const Screen = styled.View`
  flex: 1;

  padding-top: ${dynamicSize.convertSize(32)}px;
  padding-start: ${dynamicSize.convertSize(32)}px;
  padding-end: ${dynamicSize.convertSize(32)}px;

  background-color: ${({ theme }) => theme.colors.background[0]};
`;

const TabCentralize = styled.View`
  margin-top: ${dynamicSize.convertSize(10)}px;
  align-self: center;
`;

const LogoImage = styled.Image`
  width: 100%;
  height: ${dynamicSize.convertSize(15)}%;

  margin-bottom: ${dynamicSize.convertSize(30)}px;
`;
